import type { EventEmitter } from '@stencil/core';
import { Component, Element, Event, h, Host, Listen, Method, Prop, Watch } from '@stencil/core';
import type { Animation } from '../../utils/animations';
import { DISABLE_ANIMATION_VALUE } from '../../utils/animations';
import { floatingElements, VIEWPORT_ATTRIBUTE } from '../../utils/floating-elements.utils';
import { dialogCloseAnimation } from './animations/close';
import { dialogOpenAnimation } from './animations/open';

enum AnimationType {
  'CLOSE',
  'OPEN',
}
type AnimationObject = { type: AnimationType; animation: Animation };

const dialogsOpened = new Array<HTMLSyDialogElement>();
const ESCAPE_KEY = 'Escape';

/**
 * @slot - Designed to add custom content inside the dialog.
 */
@Component({
  tag: 'sy-dialog',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class Dialog {
  private backdropEl: HTMLSyBackdropElement | undefined;
  private dialogDivEl: HTMLElement | undefined;
  private currentAnimation: AnimationObject | undefined;

  /**
   * Internal boolean to indicate whether dialog is visible.
   * Used to identify when the property `visible` was modified externally.
   */
  private isVisibleInternal = false;

  @Element() el: HTMLSyDialogElement;

  /**
   * Callback function called after the dialog is closed.
   */
  @Prop() afterClosed?: (action?: any) => void | Promise<void>;

  /**
   * Callback function called after the dialog is opened.
   */
  @Prop() afterOpened?: () => void | Promise<void>;

  /**
   * Callback function called before the dialog is closed.
   * If returns 'false', the dialog will not be closed.
   */
  @Prop() beforeClosed?: (action?: any) => void | boolean | Promise<void | boolean>;

  /**
   * Callback function called before the dialog is opened.
   * If returns 'false', the dialog will not be opened.
   */
  @Prop() beforeOpened?: () => void | boolean | Promise<void | boolean>;

  /**
   * Defines the dialog size.
   */
  @Prop({ reflect: true }) dialogSize: 'small' | 'medium' | 'large' | 'extra-large' | 'full' = 'medium';

  /**
   * Whether true, the backdrop will not be displayed.
   */
  @Prop({ reflect: true }) hideBackdrop = false;

  /**
   * Dialog position style.
   * Whether fixed, the dialog will be positioned in the center of the viewport and the backdrop will fill the viewport.
   * Whether absolute, the dialog will be positioned relative to the nearest positioned ancestor and the backdrop will fill the space of the nearest positioned ancestor.
   */
  @Prop({ reflect: true }) position: 'fixed' | 'absolute' = 'fixed';

  /**
   * Whether true, the dropdown will not close with an outside click.
   */
  @Prop() ignoreOutsideClick = false;

  /**
   * If true, the dialog will not close by pressing the ESC key.
   */
  @Prop() ignoreEscape = false;

  /**
   * Defines if the dialog is visible
   */
  @Prop({ reflect: true, mutable: true }) visible = false;

  /**
   * Optional custom animation to replace the default and be played when the component starts opening.
   */
  @Prop() openAnimation?: ((sidebarElement: HTMLElement, backdropElement: HTMLElement) => Animation) | undefined;

  /**
   * Optional custom animation to replace the default and be played when the component starts closing.
   */
  @Prop() closeAnimation?: ((sidebarElement: HTMLElement, backdropElement: HTMLElement) => Animation) | undefined;

  @Watch('visible')
  visibleWatcher(): void {
    if (this.isVisibleInternal === this.visible) return;

    if (this.visible) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * Disables all animations
   */
  @Prop() disableAnimations = false;

  /**
   * Emitted after dialog opening.
   */
  @Event() syDialogDidOpen: EventEmitter<void>;

  /**
   * Emitted after dialog closing.
   * When the dialog is closed by the close method with a custom closing action identifier, the event emits the custom close action identifier.
   */
  @Event() syDialogDidClose: EventEmitter<any>;

  /**
   * Emitted after dialog submit.
   */
  @Event() syDialogDidSubmit: EventEmitter<void>;

  /**
   * Emitted after dialog cancel.
   */
  @Event() syDialogDidCancel: EventEmitter<void>;

  /**
   * Emitted when a dialog starts to close.
   */
  @Event() syDialogWillClose: EventEmitter<void>;

  /**
   * Emitted when a dialog starts to open.
   */
  @Event() syDialogWillOpen: EventEmitter<void>;

  componentDidLoad() {
    this.visibleWatcher();
  }

  /**
   * Method responsible for closing the dialog.
   */
  @Method()
  async close(action?: any, instant = false): Promise<void> {
    if (this.isVisibleInternal) {
      const beforeClosedResult = this.beforeClosed && (await this.beforeClosed(action));
      if (beforeClosedResult === false) {
        return;
      }

      if (this.currentAnimation) {
        if (this.currentAnimation.type === AnimationType.CLOSE) return;
        this.currentAnimation.animation.stop();
      }

      this.syDialogWillClose.emit();

      if (!instant && !this.disableAnimations) {
        const closeAnimation = this.getCloseAnimation();
        if (closeAnimation) {
          this.currentAnimation = { type: AnimationType.CLOSE, animation: closeAnimation };
          await this.currentAnimation.animation.play();
        }
      }

      this.isVisibleInternal = false;
      this.visible = false;
      this.onClosed(action);
    }
  }

  /**
   * Method responsible for opening the dialog.
   */
  @Method()
  async open(instant = false): Promise<void> {
    if (!this.isVisibleInternal) {
      const beforeOpenedResult = this.beforeOpened && (await this.beforeOpened());
      if (beforeOpenedResult === false) {
        return;
      }

      this.isVisibleInternal = true;
      this.visible = true;

      this.syDialogWillOpen.emit();
      await floatingElements.open(this.el);

      if (this.currentAnimation) {
        if (this.currentAnimation.type === AnimationType.OPEN) return;
        this.currentAnimation.animation.stop();
      }

      if (!instant && !this.disableAnimations) {
        const openAnimation = this.getOpenAnimation();
        if (openAnimation) {
          this.currentAnimation = { type: AnimationType.OPEN, animation: openAnimation };
          await this.currentAnimation.animation.play();
        }
      }

      this.onOpened();
    }
  }

  /**
   * Get dialog internal elements reference.
   */
  @Method()
  async getReferences(): Promise<{ [name: string]: HTMLElement | undefined }> {
    return {
      backdropElement: this.backdropEl,
    };
  }

  @Listen('syDialogHeaderDidCloseClick')
  onSyDialogHeaderDidCloseClick(ev: Event): void {
    ev?.stopImmediatePropagation();
    this.close('cancel');
  }

  @Listen('syDialogFooterDidCancelClick')
  onSyDialogFooterDidCancelClick(ev: Event): void {
    ev?.stopImmediatePropagation();
    this.close('cancel');
  }

  @Listen('syDialogFooterDidSubmitClick')
  onSyDialogFooterDidSubmitClick(ev: Event): void {
    ev?.stopImmediatePropagation();
    this.close('submit');
  }

  @Listen('keydown', { target: 'document' })
  onKeyDown(ev: KeyboardEvent): void {
    if (ev.key === ESCAPE_KEY && !this.ignoreEscape) {
      const lastOpened = dialogsOpened.at(dialogsOpened.length - 1);
      if (lastOpened === this.el) {
        ev?.stopImmediatePropagation();
        this.close('cancel');
      }
    }
  }

  @Listen('popstate', { target: 'window', passive: true })
  onPopstate(): void {
    this.close();
  }

  private async onOpened() {
    this.syDialogDidOpen.emit();

    if (this.afterOpened) {
      await this.afterOpened();
    }

    dialogsOpened.push(this.el);
  }

  private getOpenAnimation() {
    if (typeof this.openAnimation === 'string') {
      if (this.dialogDivEl && this.backdropEl) {
        return this.openAnimation === DISABLE_ANIMATION_VALUE
          ? undefined
          : dialogOpenAnimation(this.dialogDivEl, this.backdropEl);
      }
      return undefined;
    }
    const animationBuilder = this.openAnimation || dialogOpenAnimation;
    if (this.dialogDivEl && this.backdropEl) {
      return animationBuilder(this.dialogDivEl, this.backdropEl);
    }
  }

  private getCloseAnimation() {
    if (typeof this.closeAnimation === 'string') {
      if (this.dialogDivEl && this.backdropEl) {
        return this.closeAnimation === DISABLE_ANIMATION_VALUE
          ? undefined
          : dialogCloseAnimation(this.dialogDivEl, this.backdropEl);
      }
      return undefined;
    }
    const animationBuilder = this.closeAnimation || dialogCloseAnimation;
    if (this.dialogDivEl && this.backdropEl) {
      return animationBuilder(this.dialogDivEl, this.backdropEl);
    }
  }

  private async onClosed(action?: any) {
    if (action === 'cancel') this.syDialogDidCancel.emit();
    if (action === 'submit') this.syDialogDidSubmit.emit();

    if (this.afterClosed) {
      await this.afterClosed(action);
    }

    await floatingElements.close(this.el);
    this.syDialogDidClose.emit(action);

    const thisDialogIndex = dialogsOpened.findIndex((el) => el === this.el);
    dialogsOpened.splice(thisDialogIndex, 1);
  }

  private handleOutsideClick(event: Event, outsideEl: HTMLElement | undefined) {
    if (!this.ignoreOutsideClick && event.target === outsideEl) {
      event?.stopImmediatePropagation();
      this.close('cancel');
    }
  }

  render() {
    return (
      <Host {...{ [VIEWPORT_ATTRIBUTE]: true }}>
        <sy-backdrop ref={(el) => (this.backdropEl = el)} onClick={(e) => this.handleOutsideClick(e, this.backdropEl)}>
          <sy-focus-trap>
            <div class="sy-dialog" ref={(el) => (this.dialogDivEl = el)} role="dialog" aria-labelledby="title-for-aria">
              <slot></slot>
            </div>
          </sy-focus-trap>
        </sy-backdrop>
      </Host>
    );
  }
}
